import { isTrackingEnabled } from "@/lib/analytics/tracking-gate";

export type OpenAILeadEventData = {
  type: "customer_action";
  channel?: "form" | "call_us" | "whatsapp_widget";
  form?: string;
  action?: "dial" | "copy" | "chat";
  source?: string;
  page_path?: string;
  [key: string]: unknown;
};

type OpenAIConversionRequestBody = {
  id: string;
  source_url: string;
  data: OpenAILeadEventData;
};

const isOpenAIDebug = process.env.NODE_ENV !== "production";

const debugOpenAI = (...args: unknown[]) => {
  if (!isOpenAIDebug) {
    return;
  }
  console.log("[OpenAI Ads]", ...args);
};

const getPagePath = () => (typeof window !== "undefined" ? window.location.pathname : undefined);

const sendOpenAIConversionApiEvent = (body: OpenAIConversionRequestBody) => {
  debugOpenAI("CAPI request → /api/openai/conversions", body);

  void fetch("/api/openai/conversions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  })
    .then(async (response) => {
      if (!isOpenAIDebug) {
        return;
      }
      let responseBody: unknown = null;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }
      debugOpenAI("CAPI response", { status: response.status, ok: response.ok, body: responseBody });
    })
    .catch((error) => {
      debugOpenAI("CAPI fetch failed", error);
    });
};

/**
 * Fires client-side oaiq measure + server-side Conversions API lead_created.
 */
export const trackOpenAILeadCreated = (data: OpenAILeadEventData = { type: "customer_action" }) => {
  if (!isTrackingEnabled() || typeof window === "undefined") {
    return;
  }

  const eventId = crypto.randomUUID();
  const payload: OpenAILeadEventData = {
    page_path: getPagePath(),
    ...data,
    type: "customer_action",
  };

  debugOpenAI("lead_created fired", {
    eventId,
    oaiqLoaded: Boolean(window.oaiq),
    payload,
  });

  if (window.oaiq) {
    window.oaiq("measure", "lead_created", payload);
    debugOpenAI("oaiq('measure', 'lead_created', …) called");
  } else {
    debugOpenAI("oaiq not loaded — pixel measure skipped");
  }

  sendOpenAIConversionApiEvent({
    id: eventId,
    source_url: window.location.href,
    data: payload,
  });
};

export const trackOpenAIFormLead = (form: string, extra?: Omit<OpenAILeadEventData, "type" | "channel" | "form">) => {
  trackOpenAILeadCreated({
    type: "customer_action",
    channel: "form",
    form,
    ...extra,
  });
};

export const trackOpenAICallUsDial = (source: string) => {
  trackOpenAILeadCreated({
    type: "customer_action",
    channel: "call_us",
    action: "dial",
    source,
  });
};

export const trackOpenAICallUsCopy = (source: string) => {
  trackOpenAILeadCreated({
    type: "customer_action",
    channel: "call_us",
    action: "copy",
    source,
  });
};

export const trackOpenAIWhatsAppChat = (extra?: Omit<OpenAILeadEventData, "type" | "channel" | "action">) => {
  trackOpenAILeadCreated({
    type: "customer_action",
    channel: "whatsapp_widget",
    action: "chat",
    ...extra,
  });
};

/**
 * Desktop opens the phone modal; mobile dials and tracks lead_created.
 */
export const initiateCallUs = ({
  phone,
  isDesktop,
  onOpenModal,
  source,
}: {
  phone: string;
  isDesktop: boolean;
  onOpenModal: () => void;
  source: string;
}) => {
  if (isDesktop) {
    onOpenModal();
    return;
  }

  trackOpenAICallUsDial(source);
  window.location.href = `tel:${phone}`;
};
